import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { updateObjectGeneralInfo, uploadObjectPhoto, deleteObjectPhoto, getObjectPhotoUrl } from '../utils/storage';
import { ModalTemplate } from './templates';
import { useReadOnly } from '../contexts/ReadOnlyContext';
import { getL3Certification } from '../utils/l3Certification';
import './Modal.css';
import './GeneralInfoModal.css';
import './AppendixCard.css';

// Extract label-only items for a given dropdownId from pre-loaded DDL collections
function getDDLOptions(ddlCollections, dropdownId) {
  const pinnedLower = ['not applicable', 'not available'];
  const col = ddlCollections.find((c) => {
    if (!c.assignedTo) return false;
    if (Array.isArray(c.assignedTo)) return c.assignedTo.includes(dropdownId);
    return c.assignedTo === dropdownId;
  });
  if (!col) return [];
  const items = col.items || [];
  const pinned   = items.filter((it) => pinnedLower.includes(it.label.trim().toLowerCase()));
  const unpinned = items.filter((it) => !pinnedLower.includes(it.label.trim().toLowerCase()));
  return [...pinned, ...unpinned].map((it) => it.label);
}

export default function GeneralInfoModal({ object, ddlCollections = [], ddlConfigs = {}, onClose, onSaved }) {
  const readOnly = useReadOnly();   // archived → display-only "Show more"
  const existing = object.generalInfo || {};

  const [inspectionStandard, setInspectionStandard] = useState(existing.inspectionStandard || '');
  const [inspectionNumber,   setInspectionNumber]   = useState(existing.inspectionNumber   || '');
  const [startDate,          setStartDate]          = useState(existing.startDate          || '');

  // Default sameDate = true unless an existing end date differs from the start.
  // (Creating from a project auto-fills startDate but leaves endDate empty, so
  // keying off endDate keeps the box ticked by default in that flow.)
  const [sameDate, setSameDate] = useState(
    !existing.endDate ? true : existing.startDate === existing.endDate
  );
  const [endDate, setEndDate] = useState(existing.endDate || '');

  const [accountManager,       setAccountManager]       = useState(existing.accountManager       || '');
  const [leadNDTOperator,      setLeadNDTOperator]      = useState(existing.leadNDTOperator      || '');
  const [involvementL3Inspector, setInvolvementL3Inspector] = useState(existing.involvementL3Inspector || false);
  const [l3Inspector,          setL3Inspector]          = useState(existing.l3Inspector          || '');
  const [saving,               setSaving]               = useState(false);

  // DDL options — derived synchronously from pre-loaded collections (instant, no async wait)
  const standardOpts = useMemo(() => getDDLOptions(ddlCollections, 'general_inspectionStandard'), [ddlCollections]);
  const managerOpts  = useMemo(() => getDDLOptions(ddlCollections, 'general_accountManager'),     [ddlCollections]);
  const operatorOpts = useMemo(() => getDDLOptions(ddlCollections, 'general_leadNDTOperator'),    [ddlCollections]);

  // Chain-aware option resolver — mirrors getComputedOptions() in EditObjectModal
  // Reads ddlConfigs to honour the chain relationship set in the DDL Registration UI
  const gimCurrentValues = {
    general_inspectionStandard: inspectionStandard,
    general_accountManager:     accountManager,
    general_leadNDTOperator:    leadNDTOperator,
    general_l3Inspector:        l3Inspector,
  };

  function getChainedOptions(ddlId) {
    const pinnedLower = ['not applicable', 'not available'];
    const cfg = ddlConfigs[ddlId];
    if (!cfg || cfg.relationship !== 'chain' || !cfg.pointTo) {
      return getDDLOptions(ddlCollections, ddlId);
    }
    const parentValue = gimCurrentValues[cfg.pointTo] || '';
    if (!parentValue) return [];
    const collection = ddlCollections.find((c) => c.id === cfg.collectionId);
    if (!collection) return [];
    const parentItem = (collection.items || []).find((it) => it.label === parentValue);
    const children = parentItem?.children || [];
    const pinned   = children.filter((it) =>  pinnedLower.includes(it.label.trim().toLowerCase()));
    const unpinned = children.filter((it) => !pinnedLower.includes(it.label.trim().toLowerCase()));
    return [...pinned, ...unpinned].map((it) => it.label);
  }

  // L3 Inspector options — resolved through chain config (secondary children of selected standard)
  const l3InspectorOpts = useMemo(
    () => getChainedOptions('general_l3Inspector'),
    [ddlCollections, ddlConfigs, inspectionStandard], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // When inspection standard changes, cascade-clear L3 Inspector if it is chained to it.
  // Skip first render so the saved l3Inspector value survives modal open.
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (!involvementL3Inspector) return;
    const cfg = ddlConfigs['general_l3Inspector'];
    if (cfg?.relationship === 'chain' && cfg.pointTo === 'general_inspectionStandard') {
      setL3Inspector('');
    }
  }, [inspectionStandard]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Certification lookup (see utils/l3Certification) ───────────
  const certInfo = useMemo(() => {
    if (!involvementL3Inspector) return null;
    const cert = getL3Certification(ddlCollections, inspectionStandard, l3Inspector);
    if (!cert) return null;
    // No expiry date on the cert item — show name only, no validity pill
    const expiryDisplay = cert.expiryStr
      ? new Date(cert.expiryStr + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
      : null;
    return { certName: cert.certName, expiryDisplay, isExpired: cert.isExpired };
  }, [ddlCollections, involvementL3Inspector, inspectionStandard, l3Inspector]);

  // ── Photo state ────────────────────────────────────────────────
  // photoFile: new File selected by user (not yet saved)
  // photoDeleted: user deleted the existing photo (not yet saved)
  // photoPreview: object URL for newly selected file
  const [photoFile,    setPhotoFile]    = useState(null);
  const [photoDeleted, setPhotoDeleted] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [dragOver,     setDragOver]     = useState(false);
  const fileInputRef = useRef(null);

  // True if the object currently has a saved photo AND the user hasn't deleted/replaced it
  const hasSavedPhoto = object.hasPhoto && !photoDeleted;
  // Show thumbnail if saved photo still present, or if user just selected a new file
  const showThumbnail = hasSavedPhoto || photoFile !== null;
  const thumbnailSrc  = photoFile ? photoPreview : (hasSavedPhoto ? `${getObjectPhotoUrl(object.id)}?t=${object.id}` : null);

  function applyFile(file) {
    if (!file) return;
    if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
      alert('Only JPEG and PNG photos are allowed.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('Photo exceeds the 10 MB limit.');
      return;
    }
    setPhotoFile(file);
    setPhotoDeleted(false);
    const url = URL.createObjectURL(file);
    setPhotoPreview(url);
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    applyFile(file);
  }

  function handleFileInput(e) {
    applyFile(e.target.files[0]);
    e.target.value = '';
  }

  const handlePhotoDelete = useCallback(() => {
    if (photoFile) {
      URL.revokeObjectURL(photoPreview);
      setPhotoFile(null);
      setPhotoPreview(null);
    } else {
      setPhotoDeleted(true);
    }
  }, [photoFile, photoPreview]);

  // ── Handlers ──────────────────────────────────────────────────

  function handleStartDateChange(val) {
    setStartDate(val);
    if (sameDate) setEndDate(val);
  }

  function handleSameDateChange(checked) {
    setSameDate(checked);
    if (checked) {
      setEndDate(startDate); // copy start → end
    } else {
      setEndDate('');        // clear end date
    }
  }

  function handleL3CheckboxChange(checked) {
    setInvolvementL3Inspector(checked);
    if (checked) {
      setL3Inspector(''); // clear so user must actively choose
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      await updateObjectGeneralInfo(object.id, {
        inspectionStandard,
        inspectionNumber,
        startDate,
        endDate: sameDate ? startDate : endDate,
        accountManager,
        leadNDTOperator,
        involvementL3Inspector,
        l3Inspector: involvementL3Inspector ? l3Inspector : 'Not applicable',
      });
      // Handle photo changes
      if (photoFile) {
        await uploadObjectPhoto(object.id, photoFile);
      } else if (photoDeleted) {
        await deleteObjectPhoto(object.id);
      }
      onSaved?.();
      onClose();
    } catch (err) {
      console.error('Failed to save general info:', err);
      alert(err.message || 'Failed to save.');
      setSaving(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────

  return (
    <ModalTemplate
      title="Assessment Information"
      onClose={onClose}
      closeOnBackdrop={false}
      className="gim-modal"
      actions={
        readOnly ? (
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        ) : (
          <>
            <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </>
        )
      }
    >
      <div className="gim-scroll-body">
      <fieldset className="gim-fieldset" disabled={readOnly}>
        {/* Inspection Standard */}
        <div className="form-group">
          <label>Inspection Standard</label>
          <select
            className="assign-select-full"
            value={inspectionStandard}
            onChange={(e) => setInspectionStandard(e.target.value)}
          >
            <option value="">— Select —</option>
            {standardOpts.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>

        {/* Project Number */}
        <div className="form-group">
          <label>Project Number</label>
          <input
            type="text"
            className="assign-select-full"
            value={inspectionNumber}
            onChange={(e) => setInspectionNumber(e.target.value)}
            placeholder="e.g. 26-001.1"
          />
        </div>

        {/* Start Date / End Date */}
        <div className="form-row">
          <div className="form-group">
            <label>Start Date</label>
            <input
              type="date"
              className="assign-select-full"
              value={startDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>End Date</label>
            <input
              type="date"
              className={`assign-select-full${sameDate ? ' gim-date-locked' : ''}`}
              value={endDate}
              disabled={sameDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>

        {/* Same-date checkbox — sits just below the date row */}
        <div className="gim-same-date-row">
          <label className="gim-checkbox-label">
            <input
              type="checkbox"
              checked={sameDate}
              onChange={(e) => handleSameDateChange(e.target.checked)}
            />
            Start and end date are the same
          </label>
        </div>

        {/* Account Manager */}
        <div className="form-group">
          <label>Account Manager</label>
          <select
            className="assign-select-full"
            value={accountManager}
            onChange={(e) => setAccountManager(e.target.value)}
          >
            <option value="">— Select —</option>
            {managerOpts.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>

        {/* Lead NDT Operator */}
        <div className="form-group">
          <label>Lead NDT Operator</label>
          <select
            className="assign-select-full"
            value={leadNDTOperator}
            onChange={(e) => setLeadNDTOperator(e.target.value)}
          >
            <option value="">— Select —</option>
            {operatorOpts.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>

        {/* ── Involvement of L3 Inspector ── */}
        <div className="gim-l3-row form-group">
          <label className="gim-checkbox-label" style={{ marginBottom: 12 }}>
            <input
              type="checkbox"
              checked={involvementL3Inspector}
              onChange={(e) => handleL3CheckboxChange(e.target.checked)}
              disabled={saving}
            />
            Involvement of L3 Inspector
          </label>
        </div>

        {/* L3 Inspector dropdown */}
        <div className="form-group">
          <label>L3 Inspector</label>
          <select
            className="assign-select-full"
            value={involvementL3Inspector ? l3Inspector : 'Not applicable'}
            onChange={(e) => setL3Inspector(e.target.value)}
            disabled={!involvementL3Inspector || saving}
          >
            {!involvementL3Inspector ? (
              <option value="Not applicable">Not applicable</option>
            ) : (
              <>
                <option value="">— Select —</option>
                {l3InspectorOpts.map((o) => <option key={o} value={o}>{o}</option>)}
              </>
            )}
          </select>

          {/* Certification — italic small line with expiry pill below L3 Inspector */}
          {involvementL3Inspector && inspectionStandard && l3Inspector && (
            <div className="gim-cert-line">
              {certInfo ? (
                <>
                  <span>Certification: {certInfo.certName}</span>
                  {certInfo.expiryDisplay && (
                    <span className={`gim-cert-pill ${certInfo.isExpired ? 'expired' : 'valid'}`}>
                      {certInfo.isExpired ? '⚠' : '✓'} {certInfo.expiryDisplay}
                    </span>
                  )}
                </>
              ) : (
                <span className="gim-cert-no-data">No certification data</span>
              )}
            </div>
          )}
        </div>

        {/* Object Photo */}
        <div className="form-group gim-photo-group">
          <label>Object Photo</label>
          {showThumbnail ? (
            <div className="gim-photo-thumbnail-wrapper">
              <img src={thumbnailSrc} alt="Object" className="gim-photo-thumbnail" />
              <button
                type="button"
                className="appendix-delete-label gim-photo-delete"
                onClick={handlePhotoDelete}
                disabled={saving}
              >
                Delete
              </button>
            </div>
          ) : (
            <div
              className={`gim-photo-dropzone${dragOver ? ' drag-over' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
            >
              <span className="gim-photo-dropzone-text">
                Drag & drop an image here, or click to select
              </span>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png"
                style={{ display: 'none' }}
                onChange={handleFileInput}
              />
            </div>
          )}
        </div>
      </fieldset>
      </div>
    </ModalTemplate>
  );
}
